// @ts-nocheck
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Container,
  Paper,
  Tooltip,
  InputBase,
  IconButton,
  ListItem,
  List,
  ListItemText,
  Checkbox,
  styled,
  Badge,
} from "@mui/material";
import "reactjs-popup/dist/index.css";
import { useTheme } from "../ThemeContext/ThemeContext.js";
import { useHistory } from "react-router-dom";
import {
  BodyLarge,
  BodyMedium,
  H7TitleLarge,
} from "../components/StyledComponents/Typography/Typography.tsx";

import {
  PrimaryButton,
  SecondaryButton,
} from "../components/StyledComponents/Buttons/AuradineButtons.js";

import SearchIcon from "@mui/icons-material/Search";
import ActionDialog from "../components/ActionDialog/ActionDialog.js";

import useAppContextProvider from "../AppContext/useAppContextProvider.js";
import { useGetTagsList } from "../api/queries/useGetTagsList.ts";
import { usePostDeleteTag } from "./queries/usePostDeleteTag.ts";
import TagList from "./Components/TagList/TagList.js";

import { StyledCheckbox } from "../components/StyledComponents/Inputs/Inputs.js";

import {
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { set, debounce } from "lodash";
import AddTagsDialog from "./Components/AddTagsDialog/AddTagsDialog.js";
import OperationLogsDialog from "../components/OperationsLogsDialog/OperationLogsDialog.js";
import PageWrapper from "../components/PageWrapper/PageWrapper.js";

const CustomBadge = styled(Badge)(({ colors }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: colors?.text?.primary,
      color: colors?.primary?.contrastText, 
    fontSize: "0.75rem", // Optional: Adjust font size  
    padding: "0.25rem", // Optional: Adjust padding
  },
}));

export const HeaderContainer = React.memo((props) => {
  const { data, totalCount } = props;
  const { colors } = useTheme();
  const history = useHistory();

  return (
    <Box
      className={"header-container"}
      display={"flex"}
      justifyContent="space-between"
      flexDirection="column"
      gap={4}
      marginBottom={"1.5rem"}
    >
      <Box
        className={"title-box"}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
        gap={3.5}
      >
        {totalCount && totalCount !== 0 ? (
          <Box
            display={"flex"}
            flexDirection={"row"}
            justifyContent={"space-between"}
          >
            <CustomBadge
              colors={colors}
              badgeContent={totalCount}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              sx={{}}
            >
              <Box pr={3}>
                <H7TitleLarge>Tag Management</H7TitleLarge>
              </Box>
            </CustomBadge>
          </Box>
        ) : (
          <H7TitleLarge>Tag Management</H7TitleLarge>
        )}
        <BodyLarge>
          Add, delete, and view tags with their associated
          miner counts.
        </BodyLarge>
      </Box>
    </Box>
  );
});

const TagsPageHeader = (props) => {
  const {
    setOpenTagsDialog,
    tagData,
    handleTagDeletion,
    OnTagsSearch,
  } = props;
  const { colors } = useTheme();
  const selectedTags = tagData.filter(
    (t) => t.isTagSelected
  );

  const isDeleteDisabled =
    selectedTags.length === 0 ||
    selectedTags.some((tag) => tag.occurrence !== 0);

  return (
    <Box
      display={"grid"}
      justifyContent={"space-between"}
      gridTemplateColumns={"1fr 1fr"}
    >
      <Box display={"grid"} gridTemplateColumns={"2fr 1fr"}>
        <Paper
          component="form"
          elevation={0}
          sx={{
            p: "2px 4px",
            display: "flex",
            alignItems: "center",
            width: "100%",
            backgroundColor: colors?.background?.secondary,  
            borderRadius: "1.75rem",
            paddingLeft: "1rem",
          }}
        >
          <Tooltip
            // title={searchValue}
            disableFocusListener
            disableTouchListener
          >
            <InputBase
              sx={{ flex: 1, minWidth: "10.5rem", "& .MuiInputBase-input": {
                color: colors.text.primary,             // normal text
                "::placeholder": {
                  color: colors.text.hint,
                },
              } }}
              placeholder="Search by tag name"
              onChange={(event) => {
                OnTagsSearch(event);
              }}
              inputProps={{
                "aria-label": "search for tags",
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                }
              }}
            />
          </Tooltip>
          <IconButton
            type="button"
            sx={{ p: "10px" }}
            aria-label="search"
          >
            <SearchIcon sx={{ color: colors?.text?.muted }} />
          </IconButton>
        </Paper>
      </Box>
      <Box
        display={"grid"}
        gridTemplateColumns={"5fr 1fr 1fr"}
        justifyContent={"end"}
      >
        <Box></Box>

        <SecondaryButton colors={colors}
          disabled={isDeleteDisabled}
          onClick={handleTagDeletion}
        >
          Delete
        </SecondaryButton>
        <PrimaryButton colors={colors}
          onClick={() => setOpenTagsDialog(true)}
        >
          Add
        </PrimaryButton>
      </Box>
    </Box>
  );
};

/**
 * Represents the MinerPage component.
 * This component displays a table of miner devices and provides functionality to interact with them.
 */
function TagManagementContent() {
  const { colors } = useTheme();
  const {
    setToastOpen,
    setToastMessage,
    openOperationLogsDialog,
    setOpenOperationLogsDialog,
    setOperationLogs,
  } = useAppContextProvider();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    loginWithRedirect,
  } = useAuth0();
  const tableContainerRef = React.useRef(null);
  const [openTagsDialog, setOpenTagsDialog] =
    useState(false);
  const [authToken, setAuthToken] = useState("");
  const [deletionDialogOpen, setDeletionDialogOpen] =
    useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [dsearchValue, setDSearchValue] = useState("");
  const [tagData, setTagData] = React.useState([]);
  const [totalCount, setTotalCount] = useState();

  const numberOfSelections = tagData?.filter(
    (tag) => tag.isTagSelected
  ).length;

  const selectableTagsCount = tagData.filter(
    (tag) => !tag.isDefaultTag
  ).length;

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: tagsDataRefetch,
    status,
    isFetched: isTagsDataFetched,
    isLoading: isTagsDataLoading,
  } = useGetTagsList({
    authToken,
    tagName: dsearchValue,
  });

  const debouncedUpdate = React.useMemo(
    () =>
      debounce((text) => {
        setDSearchValue(text); // Update the search text state
      }, 300), // 300ms debounce delay
    []
  );

  const handleSearchChange = (event) => {
    const text = event.target.value;
    console.log("Search text:", text);
    setSearchValue(text);
    debouncedUpdate(text); // Trigger the debounced function
  };

  const { mutate: deleteTagMutate } = usePostDeleteTag();
  let limit = 25;
  // let tagData = [];
  useEffect(() => {
    if (data?.pages && isTagsDataFetched) {
      const newTagsFromApi = data.pages
        .flatMap((page, pageIndex) =>
          page.msg.map((item, index) => ({
            ...item,
            id: `${pageIndex}-${index}`, // More robust unique ID
          }))
        )
        .sort((a, b) => a.tag.localeCompare(b.tag));

      setTagData((currentTags) => {
        const selectionMap = new Map(
          currentTags
            .filter((tag) => tag.isTagSelected)
            .map((tag) => [tag.tag, true])
        );

        const finalData = newTagsFromApi.map((item) => {
          return {
            ...item,
            isTagSelected:
              selectionMap.has(item.tag) || false,
          };
        });
        return finalData;
      });
      const count = data.pages[0].count
        ? data.pages[0].count
        : 0;
      setTotalCount(count);
    }
  }, [data, isTagsDataFetched]);

  // const [numberOfSelections, setNumberOfSelections] =
  //   useState(0);

  const onTagsSearch = (e) => {};

  const onDeletionClick = () => {
    setDeletionDialogOpen(true);
  };

  const handleDeletion = async () => {
    const tags = [...tagData]
      .filter((tag) => tag.isTagSelected)
      .map((tag) => tag.tag);
    deleteTagMutate(
      { tags },
      {
        onSuccess: async (data) => {
          if (data.status === "ok") {
            setToastMessage(
              "Tag deletion operation completed. Please check the operation logs."
            );
            setOpenOperationLogsDialog(true);
            setToastOpen(true);
            await tagsDataRefetch();
            setOperationLogs(data.operationLogs);
            setOpenOperationLogsDialog(true);
          } else {
            setToastMessage(
              "Failed to delete tags. Error: " + data.msg
            );
            setToastOpen(true);
            // setNumberOfSelections(0);
          }
        },
        onError: (error) => {
          console.error("Error deleting tags:", error);
          setToastMessage("Failed to delete tags");
          setToastOpen(true);
        },
      }
    );
    setToastOpen(true);
    setOpenOperationLogsDialog(true);
    const tagsData = await tagsDataRefetch();
    setDeletionDialogOpen(false);
    // setNumberOfSelections(0);
  };

  const DeletionList = () => {
    const [deletionList, setDeletionList] = useState([]);
    useEffect(() => {
      const deletionList = tagData
        .filter((tag) => tag.isTagSelected)
        .map((tag) => tag.tag);
      setDeletionList(deletionList);
    }, [tagData]);
    return (
      <List sx={{ maxHeight: "12rem", overflowY: "auto" }}>
        {deletionList.map((itemToBeDeleted, index) => (
          <ListItem
            button={false}
            key={index}
            sx={{ borderBottom: "1px solid " + colors?.border?.default }}
          >
            <ListItemText>
              <BodyLarge>{itemToBeDeleted}</BodyLarge>
            </ListItemText>
          </ListItem>
        ))}
      </List>
    );
  };

  const handleParentCheckboxSelection = (event) => {
    const checked = event.target.checked;
    setTagData((prevTagData) =>
      prevTagData.map((tag) => ({
        ...tag,
        isTagSelected: checked ? !tag.isDefaultTag : false,
      }))
    );
  };

  return (
    <PageWrapper> 
      <HeaderContainer totalCount={totalCount} />
      <Grid
        container
        className={"miner-page-table-container"}
        display={"flex"}
        flexDirection={"row"}
        spacing={4}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <Grid
          ref={tableContainerRef}
          item
          xs={12}
          sx={{
            transition: "width 0.3s ease-in-out", // Smooth transition for width
          }}
        >
          <Box
            className={"Tag-management-table"}
            display={"flex"}
            flexDirection={"row"}
            backgroundColor={colors?.background?.paper}
            padding={"0.75rem 0.75rem"}
            borderRadius={"1rem"}
            sx={{
              transition: "width 0.3s ease-in-out", // Smooth transition for width
            }}
          >
            <Box
              className={"table-container"}
              display={"flex"}
              flexDirection={"column"}
              height={"auto"}
              minWidth={"0"}
              gap={4}
              flex={1}
              flexShrink={1}
            >
              <TagsPageHeader
                OnTagsSearch={handleSearchChange}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                handleTagDeletion={onDeletionClick}
                tagData={tagData}
                onDeletionClick={onDeletionClick}
                setOpenTagsDialog={setOpenTagsDialog}
                // setTagData={setTagData}
              />
              <Box
                className={"Datagrid-container"}
                display={"flex"}
                flexDirection={"column"}
                flexShrink={1}
                flexGrow={1}
                // overflowY={"auto"}
                height="calc(100vh - 17rem)"
              >
                {tagData.length > 0 && (
                  <Box
                  display="grid"
                  gridTemplateColumns={"2rem auto"}
                  justifySelf={"start"}
                  alignItems={"center"}
                  columnGap={2}
                  marginLeft={"1.30rem"}
                    sx={{
                      cursor: "pointer",
                      transition: "all 0.3s ease-in-out",
                      borderRadius: "6.25rem",
                      "&:hover": {
                        backgroundColor: colors?.hover?.muted,
                      }
                    }}
                  >
                    <StyledCheckbox
                      colors={colors}
                      onChange={(e) =>
                        handleParentCheckboxSelection(e)
                      }
                      indeterminate={
                        numberOfSelections > 0 &&
                        numberOfSelections <
                          selectableTagsCount
                      }
                      checked={
                        selectableTagsCount > 0 &&
                        numberOfSelections ===
                          selectableTagsCount
                      }
                    />
                    <BodyMedium>{`${numberOfSelections} items selected`}</BodyMedium>
                  </Box>
                )}
                <TagList
                  tagList={tagData}
                  isTagsDataLoading={isTagsDataLoading}
                  isTagsDataFetched={isTagsDataFetched}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  setTagData={setTagData}
                  // setNumberOfSelections={
                  //   setNumberOfSelections
                  // }
                  attachTagsMode={false}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      <AddTagsDialog
        open={openTagsDialog}
        setOpen={setOpenTagsDialog}
        refetch={tagsDataRefetch}
      />
      <ActionDialog
        open={deletionDialogOpen}
        onClose={() => setDeletionDialogOpen(false)}
        headerMessage={"Delete these tags?"}
        subMessage={
          "Deleting the tags removes it from FluxVision. This action cannot be undone."
        }
        buttonText={"Delete"}
        deleteAction={handleDeletion}
        extraInfo={true}
        ExtraInfoComponent={DeletionList}
      />
      <OperationLogsDialog
        open={openOperationLogsDialog}
        setOpen={setOpenOperationLogsDialog}
      />
    </PageWrapper>
  );
}

export default function TagManagementPage() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: 0,
      },
      mutations: {
        retry: 0,
      },
    },
  });
  return (
    <QueryClientProvider client={queryClient}>
      <TagManagementContent />
    </QueryClientProvider>
  );
}
